import React, { useEffect, useState } from 'react';
import { useInterval, useLocalStorage } from 'usehooks-ts';

import {
  SessionAssertByConnectorRequestSourceTypeEnum,
  SessionCreationByConnectorRequestSourceTypeEnum,
  SessionModel,
  SessionProviderModel,
  SessionProviderStatus,
  SessionStatus,
} from '../../../gamma-api';
import { defaultApi } from '../../apis';

import { AUTH_CHECK_INTERVAL, AUTH_SESSION_ID_HEADER_NAME } from './const';
import { AuthSessionEvent, AuthSessionStatus, IAuthSessionState } from './types';
import { buildSessionKey } from './utils';

interface IAuthSessionProps {
  status: AuthSessionStatus;
  connectorId?: string;
  onChange?: (state: IAuthSessionState) => void;
}

const AuthSession: React.FC<IAuthSessionProps> = ({ status, connectorId, onChange }) => {
  const sessionKey = buildSessionKey();
  const [sessionId, setSessionId] = useLocalStorage<string>(sessionKey, '');

  const [session, setSession] = useState<SessionModel | null>();
  const [providers, setProviders] = useState<SessionProviderModel[]>([]);

  const calculateStatus = (newSession: SessionModel): AuthSessionStatus => {
    if (newSession.status === SessionStatus.SignedIn) {
      return AuthSessionStatus.SignedIn;
    }

    if (status === AuthSessionStatus.Redirection) {
      return status;
    }

    if (newSession.status === SessionStatus.Challenged) {
      return AuthSessionStatus.Challenged;
    }

    return status;
  };

  const calculateEvent = (oldStatus: AuthSessionStatus, newStatus: AuthSessionStatus): AuthSessionEvent | undefined => {
    if (oldStatus === AuthSessionStatus.Challenged && newStatus === AuthSessionStatus.Redirection) {
      return AuthSessionEvent.Redirected;
    }

    if (oldStatus === AuthSessionStatus.Redirection && newStatus === AuthSessionStatus.SignedIn) {
      return AuthSessionEvent.SignedIn;
    }

    return undefined;
  };

  const applyChanges = (
    newStatus: AuthSessionStatus,
    newSession?: SessionModel | null,
    newProviders?: SessionProviderModel[],
    newEvent?: AuthSessionEvent
  ) => {
    setSession(newSession || null);
    setProviders(newProviders || []);

    if (!onChange) {
      return;
    }

    const newOptions = newSession ? { headers: { [AUTH_SESSION_ID_HEADER_NAME]: newSession.session_id } } : undefined;
    const newState: IAuthSessionState = { status: newStatus, event: newEvent, options: newOptions };
    onChange?.(newState);
  };

  const loadSessionAsync = async () => {
    if (!connectorId) {
      applyChanges(AuthSessionStatus.Unknown);
      return;
    }

    try {
      applyChanges(AuthSessionStatus.Loading);
      const sessionRequiredResponse = await defaultApi.getSessionRequirement(null, connectorId);
      if (!sessionRequiredResponse.data.required) {
        applyChanges(AuthSessionStatus.NotRequired);
        return;
      }

      let newSession: SessionModel;
      if (sessionId) {
        const sessionUpdateResponse = await defaultApi.assertSession(sessionId, {
          source_type: SessionAssertByConnectorRequestSourceTypeEnum.Connector,
          connector_id: connectorId,
        });
        newSession = sessionUpdateResponse.data.model;
      } else {
        const sessionCreationResponse = await defaultApi.createSession({
          source_type: SessionCreationByConnectorRequestSourceTypeEnum.Connector,
          connector_id: connectorId,
        });
        newSession = sessionCreationResponse.data.model;
        setSessionId(sessionCreationResponse.data.model.session_id);
      }
      const newStatus = calculateStatus(newSession);
      applyChanges(newStatus, newSession);
    } catch (err) {
      setSessionId('');
      // eslint-disable-next-line no-console
      console.error(err);
      applyChanges(AuthSessionStatus.Unknown);
    }
  };

  const loadSessionSync = () => {
    loadSessionAsync().finally();
  };

  useEffect(loadSessionSync, [connectorId]);

  const checkSessionAsync = async () => {
    if (!session || !providers.length) {
      return;
    }

    try {
      const sessionResponse = await defaultApi.getSession(session.session_id);
      const newSession = sessionResponse.data;
      const responseProviders = newSession.providers.filter((np) =>
        providers.some((p) => p.authority === np.authority)
      );
      const newProviders = responseProviders.some((rp) => rp.status === SessionProviderStatus.SignedIn)
        ? providers.filter((p) =>
            responseProviders.some((rp) => rp.authority === p.authority && rp.status !== SessionProviderStatus.SignedIn)
          )
        : providers;
      const newStatus = calculateStatus(newSession);
      const newEvent = calculateEvent(status, newStatus);
      applyChanges(newStatus, newSession, newProviders, newEvent);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const checkSessionSync = () => {
    checkSessionAsync().finally();
  };

  useInterval(checkSessionSync, AUTH_CHECK_INTERVAL);

  const onStatusPropChange = () => {
    if (status !== AuthSessionStatus.Redirection) {
      return;
    }

    const provider = session?.providers.find((provider) => provider.status === SessionProviderStatus.Challenged);
    if (!provider) {
      return;
    }

    // eslint-disable-next-line security/detect-non-literal-fs-filename
    provider.redirect && window.open(provider.redirect, '_blank');
    const newProviders = [...providers, provider];
    applyChanges(status, session, newProviders, AuthSessionEvent.Redirected);
  };

  useEffect(onStatusPropChange, [status]);

  return null;
};

export default AuthSession;
